import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import { H4 } from '@liveauctioneers/hammer-ui-core/text';
import { memo } from 'react';
import isEqual from 'react-fast-compare';
import { useNavigate } from 'react-router-dom';

import PageTitle from '@/components/PageTitle/PageTitle';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { type TranslationBlock, useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { modifyAuctionCalendarParamsSaleStart, modifyParamsRemovePage } from '@/utils/queryParams';
import { getAuctionCalendarUrlWithParams } from '@/utils/urls';

type Props = {
    queryParams: {
        page: number;
        pageNumber: number;
        sort: SearchSortAndDirection;
    };
    totalFound: number;
};

const AuctionCalendarPageHeaderComponent = ({ queryParams, totalFound }: Props) => {
    const formatMessage = useAppFormatMessage();
    const navigate = useNavigate();
    const soonest = Boolean(queryParams.sort) && queryParams.sort === SearchSortAndDirection.TimeMostRecent;

    const onChangeSortOption = (event) => {
        if (event.value === SearchSortAndDirection.TimeStartingSoonest && soonest) {
            return;
        }
        const getSoonest = event.value === SearchSortAndDirection.TimeStartingSoonest;
        let newParams = modifyAuctionCalendarParamsSaleStart(queryParams, getSoonest);
        newParams = modifyParamsRemovePage(newParams);
        const url = getAuctionCalendarUrlWithParams(newParams);
        navigate(url);
    };

    const options = [
        {
            label: String(formatMessage(['auctionCalendar', 'sort', 'trending'])),
            value: 'trending',
        },
        {
            label: String(
                formatMessage(['sorts', SearchSortAndDirection.TimeStartingSoonest as keyof TranslationBlock['sorts']])
            ),
            value: SearchSortAndDirection.TimeStartingSoonest,
        },
    ];

    return (
        <div className="w-[calc(100%-40px)] max-w-[1200px] self-center">
            <div className="mt-sm mb-md flex flex-row items-end justify-between xsMax:mb-xs">
                <PageTitle
                    className="m-0 flex items-center xsMax:flex-row xsMax:flex-wrap xsMax:items-baseline"
                    title={formatMessage(['auction_calendar'])}
                />
            </div>
            <div
                className="relative flex flex-1 flex-row items-center py-[20px] [&_h4]:mr-xs [&_h4]:flex-none [&_h4]:whitespace-nowrap"
                key="auction-calendar-search-page-header-row"
            >
                <div
                    className="flex w-full items-center justify-between bg-background-primary p-0 xsMax:flex-col xsMax:items-start"
                    key="auction-calendar-search-page-header"
                >
                    <H4 key="auction-calendar-search-bar-text">
                        {formatMessage(['auctionCalendar', 'searchBarText'], {
                            values: {
                                count: (
                                    <FormattedNumber
                                        key="total_found"
                                        value={totalFound}
                                    />
                                ),
                            },
                        })}
                    </H4>
                    <div className="flex flex-1 justify-end">
                        <Select
                            className="min-w-[246px] xsMax:mt-xs"
                            data-testid="choose-sort-dropdown"
                            isInsideLabel
                            labelText={formatMessage(['auctionCalendar', 'sort', 'title'])}
                            name="sort"
                            onChange={onChangeSortOption}
                            options={options}
                            placeholder={formatMessage(['select'])}
                            small
                            value={soonest ? options[1] : options[0]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AuctionCalendarPageHeader = memo<Props>(AuctionCalendarPageHeaderComponent, isEqual);
